import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Tag, Card, Image, Drawer } from 'antd';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const LawyerVerification = () => {
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPendingLawyers();
  }, []);

  const fetchPendingLawyers = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'lawyers'),
        where('verificationStatus', '==', 'pending')
      );
      const snapshot = await getDocs(q);
      setLawyers(
        snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    } catch (error) {
      console.error('Error fetching lawyers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (lawyerId, status, note) => {
    try {
      const verifyLawyer = httpsCallable(functions, 'verifyLawyer');
      await verifyLawyer({
        lawyerId,
        status,
        adminNote: note,
      });

      // Refresh list
      fetchPendingLawyers();
      setDrawerVisible(false);
      Modal.success({
        title: 'Success',
        content: `Lawyer has been ${status}`,
      });
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: error.message,
      });
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (text, record) => (
        <span>
          {text}
          {record.isVerified && <Tag color="green" style={{ marginLeft: 8 }}>Verified</Tag>}
        </span>
      ),
    },
    {
      title: 'Bar Council ID',
      dataIndex: 'barCouncilId',
      key: 'barCouncilId',
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
    },
    {
      title: 'Experience',
      dataIndex: 'experienceYears',
      key: 'experienceYears',
      render: (years) => `${years} years`,
    },
    {
      title: 'Practice Areas',
      dataIndex: 'practiceAreas',
      key: 'practiceAreas',
      render: (areas) => areas.join(', '),
    },
    {
      title: 'Status',
      dataIndex: 'verificationStatus',
      key: 'status',
      render: (status) => {
        let color = status === 'verified' ? 'green' : status === 'rejected' ? 'red' : 'orange';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => {
              setSelectedLawyer(record);
              setDrawerVisible(true);
            }}
          >
            Review
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600 }}>Therapist Verification</h1>
      <Card>
        <Table
          columns={columns}
          dataSource={lawyers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Drawer
        title="Review Lawyer"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={500}
      >
        {selectedLawyer && (
          <div>
            <p><strong>Name:</strong> {selectedLawyer.displayName}</p>
            <p><strong>Bar Council ID:</strong> {selectedLawyer.barCouncilId}</p>
            <p><strong>State:</strong> {selectedLawyer.state}</p>
            <p><strong>Experience:</strong> {selectedLawyer.experienceYears} years</p>
            <p><strong>Practice Areas:</strong> {selectedLawyer.practiceAreas.join(', ')}</p>
            <p><strong>Languages:</strong> {selectedLawyer.languages.join(', ')}</p>
            <p><strong>Bio:</strong> {selectedLawyer.bio}</p>

            <h3>Verification Documents</h3>
            {/* Document preview would go here */}

            <Form layout="vertical" style={{ marginTop: 24 }}>
              <Form.Item label="Admin Note">
                <Input.TextArea
                  rows={4}
                  placeholder="Enter verification note or rejection reason"
                />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => handleVerify(selectedLawyer.id, 'verified')}
                  >
                    Approve
                  </Button>
                  <Button
                    type="primary"
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => {
                      const note = form.getFieldValue('note');
                      handleVerify(selectedLawyer.id, 'rejected', note);
                    }}
                  >
                    Reject
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default LawyerVerification;
